package org.teddapp.project.ui.projects

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Organization
import com.tedd.db.Project
import com.tedd.db.ProjectStatus
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import com.tedd.db.Skill
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.TeddTokens

internal val ProjectColorPalette = listOf(
    "#6366F1", "#22D3EE", "#10B981", "#F59E0B",
    "#EF4444", "#EC4899", "#8B5CF6", "#0EA5E9",
)

private val OrgIconPalette = listOf(
    "target", "users", "book", "spark",
    "heart", "flame", "wallet", "calendar",
)

private val ProjectIconPalette = listOf(
    "target", "focus", "spark", "flame",
    "grid", "list", "note", "calendar",
)

/**
 * Modal "Gérer les organisations" — liste avec edit/trash toujours visibles
 * (pas de hover, cohérent avec CategoryManagerDialog des Objectifs)
 * + form d'ajout d'une nouvelle organisation.
 */
@Composable
fun OrganizationsManagerDialog(
    organizations: List<Organization>,
    onClose: () -> Unit,
    onEditOrg: (Organization) -> Unit,
    onDeleteOrg: (Organization) -> Unit,
    onCreateOrg: (name: String, color: String, icon: String) -> Unit,
) {
    var newName by remember { mutableStateOf("") }
    var newColor by remember { mutableStateOf(ProjectColorPalette[0]) }
    var newIcon by remember { mutableStateOf("target") }

    val trimmed = newName.trim()
    val nameTaken = trimmed.isNotEmpty() &&
        organizations.any { it.name.equals(trimmed, ignoreCase = true) }
    val canAdd = trimmed.isNotEmpty() && !nameTaken

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.42f))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClose,
            ),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .widthIn(min = 440.dp, max = 520.dp)
                .heightIn(min = 360.dp, max = 620.dp)
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = {},
                ),
        ) {
            FormHeader(
                label = "ORGANISATIONS",
                title = "Gérer mes organisations",
                onClose = onClose,
            )

            // List
            Column(
                modifier = Modifier
                    .weight(1f)
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = 22.dp, vertical = 14.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                if (organizations.isEmpty()) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                            .padding(16.dp),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            text = "Aucune organisation. Crée-en une ci-dessous.",
                            color = TeddTokens.InkMuted,
                            fontSize = 12.sp,
                        )
                    }
                } else {
                    organizations.forEach { org ->
                        val color = runCatching { Color(parseHexCrud(org.color)) }.getOrElse { TeddTokens.LimeStrong }
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(TeddTokens.RSm))
                                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                                .padding(horizontal = 12.dp, vertical = 10.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(10.dp),
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(28.dp)
                                    .clip(RoundedCornerShape(TeddTokens.RSm))
                                    .background(color.copy(alpha = 0.15f), RoundedCornerShape(TeddTokens.RSm)),
                                contentAlignment = Alignment.Center,
                            ) {
                                TeddIcon(name = org.icon, size = 14.dp, color = color, strokeWidth = 2.dp)
                            }
                            Text(
                                text = org.name,
                                color = TeddTokens.Ink,
                                fontSize = 13.sp,
                                fontWeight = FontWeight.Medium,
                                modifier = Modifier.weight(1f),
                            )
                            TeddIconButton(onClick = { onEditOrg(org) }, size = 28.dp) {
                                TeddIcon(name = "edit", size = 13.dp, color = TeddTokens.InkMuted)
                            }
                            TeddIconButton(onClick = { onDeleteOrg(org) }, size = 28.dp) {
                                TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.InkMuted)
                            }
                        }
                    }
                }
            }

            // Add form
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                    .background(TeddTokens.SurfaceSunken)
                    .padding(22.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Text(
                    text = "AJOUTER UNE ORGANISATION",
                    color = TeddTokens.InkMuted,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = 0.6.sp,
                )
                EntityNameField(
                    value = newName,
                    onValueChange = { newName = it },
                    placeholder = "Nom de l'organisation",
                    errorMessage = if (nameTaken) "Une organisation avec ce nom existe déjà." else null,
                )
                EntityColorPicker(value = newColor, onChange = { newColor = it })
                EntityIconPicker(
                    palette = listOf("target", "users", "book", "spark", "heart", "flame", "wallet", "calendar"),
                    value = newIcon,
                    color = newColor,
                    onChange = { newIcon = it },
                )
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TeddButton(
                        text = "Ajouter",
                        onClick = {
                            onCreateOrg(trimmed, newColor, newIcon)
                            newName = ""
                            newColor = ProjectColorPalette[0]
                            newIcon = "target"
                        },
                        kind = TeddButtonKind.Primary,
                        size = TeddButtonSize.Md,
                        enabled = canAdd,
                        leadingIcon = {
                            TeddIcon(name = "plus", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.2.dp)
                        },
                    )
                }
            }
        }
    }
}

/**
 * Form dialog pour créer / modifier une organisation.
 * Validation : name non-vide + unique (case-insensitive) parmi `existingNames`.
 */
@Composable
fun OrganizationFormDialog(
    initial: Organization?,
    existingNames: List<String>,
    onClose: () -> Unit,
    onSave: (name: String, color: String, icon: String) -> Unit,
) {
    val isNew = initial == null
    var name by remember(initial) { mutableStateOf(initial?.name ?: "") }
    var color by remember(initial) { mutableStateOf(initial?.color ?: ProjectColorPalette[0]) }
    var icon by remember(initial) { mutableStateOf(initial?.icon ?: OrgIconPalette[0]) }

    val trimmed = name.trim()
    val nameTaken = trimmed.isNotEmpty() &&
        existingNames.any { it.equals(trimmed, ignoreCase = true) }
    val canSave = trimmed.isNotEmpty() && !nameTaken

    CenterModal(onDismiss = onClose) {
        FormHeader(
            label = if (isNew) "NOUVELLE ORGANISATION" else "MODIFIER L'ORGANISATION",
            title = if (isNew) "Crée un espace" else name.ifBlank { "Sans titre" },
            onClose = onClose,
        )
        Column(
            modifier = Modifier
                .weight(1f, fill = true)
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(22.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            EntityNameField(
                value = name,
                onValueChange = { name = it },
                placeholder = "Nom de l'organisation",
                errorMessage = if (nameTaken) "Une organisation avec ce nom existe déjà." else null,
            )
            EntityColorPicker(value = color, onChange = { color = it })
            EntityIconPicker(palette = OrgIconPalette, value = icon, color = color, onChange = { icon = it })
        }
        FormFooter(
            canSave = canSave,
            isNew = isNew,
            onCancel = onClose,
            onSave = { onSave(trimmed, color, icon) },
        )
    }
}

/**
 * Form dialog pour créer / modifier un projet (dans une organisation donnée).
 * Validation : name non-vide + unique au sein des projets de la même org.
 */
@Composable
fun ProjectFormDialog(
    initial: Project?,
    organizationName: String,
    existingNames: List<String>,
    skills: List<Skill> = emptyList(),
    onClose: () -> Unit,
    onSave: (name: String, color: String, icon: String, description: String, objectives: String, skillId: String?) -> Unit,
) {
    val isNew = initial == null
    var name by remember(initial) { mutableStateOf(initial?.name ?: "") }
    var color by remember(initial) { mutableStateOf(initial?.color ?: ProjectColorPalette[0]) }
    var icon by remember(initial) { mutableStateOf(initial?.icon ?: ProjectIconPalette[0]) }
    var description by remember(initial) { mutableStateOf(initial?.description ?: "") }
    var objectives by remember(initial) { mutableStateOf(initial?.objectives ?: "") }
    var skillId by remember(initial) { mutableStateOf(initial?.skill_id) }

    val trimmed = name.trim()
    val nameTaken = trimmed.isNotEmpty() &&
        existingNames.any { it.equals(trimmed, ignoreCase = true) }
    val canSave = trimmed.isNotEmpty() && !nameTaken

    CenterModal(onDismiss = onClose) {
        FormHeader(
            label = if (isNew) "NOUVEAU PROJET · $organizationName" else "MODIFIER LE PROJET",
            title = if (isNew) "Crée un projet" else name.ifBlank { "Sans titre" },
            onClose = onClose,
        )
        Column(
            modifier = Modifier
                .weight(1f, fill = true)
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(22.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            EntityNameField(
                value = name,
                onValueChange = { name = it },
                placeholder = "Nom du projet",
                errorMessage = if (nameTaken) "Un projet avec ce nom existe déjà dans cette organisation." else null,
            )
            if (skills.isNotEmpty()) {
                ProjectSkillPicker(skills = skills, selectedId = skillId, onSelect = { skillId = if (skillId == it) null else it })
            }
            EntityDescriptionField(
                value = description,
                onValueChange = { description = it },
            )
            EntityDescriptionField(
                value = objectives,
                onValueChange = { objectives = it },
                label = "Objectifs",
                placeholder = "Ce que ce projet doit accomplir (optionnel)…",
            )
            EntityColorPicker(value = color, onChange = { color = it })
            EntityIconPicker(palette = ProjectIconPalette, value = icon, color = color, onChange = { icon = it })
        }
        FormFooter(
            canSave = canSave,
            isNew = isNew,
            onCancel = onClose,
            onSave = { onSave(trimmed, color, icon, description.trim(), objectives.trim(), skillId) },
        )
    }
}

/**
 * Form dialog pour créer / modifier une colonne Kanban (ProjectStatus).
 * Validation : name non-vide + unique au sein du projet.
 */
@Composable
fun ProjectStatusFormDialog(
    initial: ProjectStatus?,
    existingNames: List<String>,
    onClose: () -> Unit,
    onSave: (name: String, color: String, isTerminal: Boolean) -> Unit,
) {
    val isNew = initial == null
    var name by remember(initial) { mutableStateOf(initial?.name ?: "") }
    var color by remember(initial) { mutableStateOf(initial?.color ?: ProjectColorPalette[0]) }
    var isTerminal by remember(initial) { mutableStateOf(initial?.is_terminal == 1L) }

    val trimmed = name.trim()
    val nameTaken = trimmed.isNotEmpty() &&
        existingNames.any { it.equals(trimmed, ignoreCase = true) }
    val canSave = trimmed.isNotEmpty() && !nameTaken

    CenterModal(onDismiss = onClose) {
        FormHeader(
            label = if (isNew) "NOUVELLE COLONNE" else "MODIFIER LA COLONNE",
            title = if (isNew) "Crée une colonne Kanban" else name.ifBlank { "Sans titre" },
            onClose = onClose,
        )
        Column(
            modifier = Modifier
                .weight(1f, fill = true)
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(22.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            EntityNameField(
                value = name,
                onValueChange = { name = it },
                placeholder = "Nom de la colonne (À faire, En cours, Terminé…)",
                errorMessage = if (nameTaken) "Une colonne avec ce nom existe déjà." else null,
            )
            EntityColorPicker(value = color, onChange = { color = it })
            // Terminal toggle
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(
                        if (isTerminal) TeddTokens.Success.copy(alpha = 0.08f) else TeddTokens.SurfaceSunken,
                        RoundedCornerShape(TeddTokens.RSm),
                    )
                    .border(
                        1.dp,
                        if (isTerminal) TeddTokens.Success.copy(alpha = 0.35f) else TeddTokens.Line,
                        RoundedCornerShape(TeddTokens.RSm),
                    )
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = { isTerminal = !isTerminal },
                    )
                    .padding(12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Box(
                    modifier = Modifier
                        .size(18.dp)
                        .clip(RoundedCornerShape(TeddTokens.RXs))
                        .then(
                            if (isTerminal) Modifier.background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RXs))
                            else Modifier.border(1.5.dp, TeddTokens.InkSubtle, RoundedCornerShape(TeddTokens.RXs))
                        ),
                    contentAlignment = Alignment.Center,
                ) {
                    if (isTerminal) {
                        TeddIcon(name = "check", size = 11.dp, color = TeddTokens.OnAction, strokeWidth = 2.5.dp)
                    }
                }
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = "Colonne terminale",
                        color = TeddTokens.Ink,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.SemiBold,
                    )
                    Text(
                        text = "Les tâches dans cette colonne seront considérées comme faites (status = done).",
                        color = TeddTokens.InkMuted,
                        fontSize = 11.5.sp,
                    )
                }
            }
        }
        FormFooter(
            canSave = canSave,
            isNew = isNew,
            onCancel = onClose,
            onSave = { onSave(trimmed, color, isTerminal) },
        )
    }
}

private val NoteColorPalette = listOf<String?>(
    null,           // pas de couleur
    "#FEF3C7",      // amber soft
    "#D1FAE5",      // mint soft
    "#DBEAFE",      // sky soft
    "#FCE7F3",      // pink soft
    "#EDE9FE",      // lilac soft
)


/**
 * Confirm delete générique — affiche un message custom et un nom à supprimer.
 */
@Composable
fun ProjectsConfirmDelete(
    title: String,
    itemName: String,
    description: String,
    onCancel: () -> Unit,
    onConfirm: () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.42f))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onCancel,
            ),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .width(380.dp)
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = {},
                )
                .padding(22.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                Box(
                    modifier = Modifier
                        .size(40.dp)
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(TeddTokens.DangerSoft, RoundedCornerShape(TeddTokens.RSm)),
                    contentAlignment = Alignment.Center,
                ) {
                    TeddIcon(name = "trash", size = 18.dp, color = TeddTokens.Danger)
                }
                Column {
                    Text(title, color = TeddTokens.Ink, fontSize = 15.sp, fontWeight = FontWeight.SemiBold)
                    Text(description, color = TeddTokens.InkMuted, fontSize = 12.sp)
                }
            }
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .padding(12.dp),
            ) {
                Text("« $itemName »", color = TeddTokens.Ink, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End),
            ) {
                TeddButton(text = "Annuler", onClick = onCancel,
                    kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md)
                TeddButton(text = "Supprimer", onClick = onConfirm,
                    kind = TeddButtonKind.Danger, size = TeddButtonSize.Md)
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Shared form pieces
// ──────────────────────────────────────────────────────────────────────

@Composable
private fun CenterModal(
    onDismiss: () -> Unit,
    content: @Composable androidx.compose.foundation.layout.ColumnScope.() -> Unit,
) {
    val fullScreen = org.teddapp.project.ui.LocalUseFullScreenForm.current
    if (fullScreen) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(TeddTokens.Bg)
                .imePadding(),
        ) {
            content()
        }
    } else {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Color.Black.copy(alpha = 0.42f))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onDismiss,
                ),
            contentAlignment = Alignment.Center,
        ) {
            Column(
                modifier = Modifier
                    .widthIn(min = 440.dp, max = 520.dp)
                    .heightIn(min = 360.dp, max = 600.dp)
                    .clip(RoundedCornerShape(TeddTokens.RMd))
                    .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = {},
                    ),
            ) {
                content()
            }
        }
    }
}

@Composable
internal fun FormHeader(label: String, title: String, onClose: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
            .padding(horizontal = 22.dp, vertical = 14.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = label,
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                letterSpacing = 0.6.sp,
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = title,
                color = TeddTokens.Ink,
                fontSize = 16.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        TeddIconButton(onClick = onClose, size = 30.dp) {
            Box(modifier = Modifier.rotate(45f)) {
                TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted, strokeWidth = 1.8.dp)
            }
        }
    }
}

@Composable
private fun FormFooter(canSave: Boolean, isNew: Boolean, onCancel: () -> Unit, onSave: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
            .padding(16.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End),
    ) {
        TeddButton(text = "Annuler", onClick = onCancel,
            kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md)
        TeddButton(
            text = if (isNew) "Créer" else "Enregistrer",
            onClick = onSave,
            kind = TeddButtonKind.Primary,
            size = TeddButtonSize.Md,
            enabled = canSave,
            leadingIcon = { TeddIcon(name = "check", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.2.dp) },
        )
    }
}

@Composable
internal fun EntityNameField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    errorMessage: String? = null,
) {
    val hasError = errorMessage != null
    Column {
        Row {
            Text("Nom", color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
            Text(" *", color = TeddTokens.Danger, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
        }
        Spacer(modifier = Modifier.height(6.dp))
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(38.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                .border(
                    1.dp,
                    if (hasError) TeddTokens.Danger.copy(alpha = 0.5f) else TeddTokens.Line,
                    RoundedCornerShape(TeddTokens.RSm),
                )
                .padding(horizontal = 12.dp),
            contentAlignment = Alignment.CenterStart,
        ) {
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                singleLine = true,
                textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                decorationBox = { inner ->
                    if (value.isEmpty()) {
                        Text(placeholder, color = TeddTokens.InkSubtle, fontSize = 13.sp)
                    }
                    inner()
                },
            )
        }
        if (errorMessage != null) {
            Text(
                text = errorMessage,
                color = TeddTokens.Danger,
                fontSize = 11.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
    }
}

@Composable
private fun EntityDescriptionField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String = "Description",
    placeholder: String = "Brève description (optionnel)…",
) {
    Column {
        Text(label, color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
        Spacer(modifier = Modifier.height(6.dp))
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = 56.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .padding(horizontal = 12.dp, vertical = 10.dp),
            contentAlignment = Alignment.TopStart,
        ) {
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp, lineHeight = 18.sp),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                decorationBox = { inner ->
                    if (value.isEmpty()) {
                        Text(placeholder, color = TeddTokens.InkSubtle, fontSize = 13.sp)
                    }
                    inner()
                },
            )
        }
    }
}

@Composable
internal fun EntityColorPicker(value: String, onChange: (String) -> Unit) {
    Column {
        Text("Couleur", color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
        Spacer(modifier = Modifier.height(8.dp))
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            ProjectColorPalette.forEach { hex ->
                val c = runCatching { Color(parseHexCrud(hex)) }.getOrElse { TeddTokens.LimeStrong }
                val selected = value.equals(hex, ignoreCase = true)
                Box(
                    modifier = Modifier
                        .size(if (selected) 32.dp else 28.dp)
                        .clip(CircleShape)
                        .background(c, CircleShape)
                        .then(
                            if (selected) Modifier.border(2.dp, TeddTokens.Ink, CircleShape)
                            else Modifier
                        )
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onChange(hex) },
                        )
                )
            }
        }
    }
}

@Composable
internal fun EntityIconPicker(
    palette: List<String>,
    value: String,
    color: String,
    onChange: (String) -> Unit,
) {
    Column {
        Text("Icône", color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
        Spacer(modifier = Modifier.height(8.dp))
        val c = runCatching { Color(parseHexCrud(color)) }.getOrElse { TeddTokens.LimeStrong }
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            palette.forEach { iconName ->
                val selected = value == iconName
                Box(
                    modifier = Modifier
                        .size(36.dp)
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(
                            if (selected) c.copy(alpha = 0.18f) else TeddTokens.SurfaceSunken,
                            RoundedCornerShape(TeddTokens.RSm),
                        )
                        .border(
                            1.dp,
                            if (selected) c else TeddTokens.Line,
                            RoundedCornerShape(TeddTokens.RSm),
                        )
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onChange(iconName) },
                        ),
                    contentAlignment = Alignment.Center,
                ) {
                    TeddIcon(
                        name = iconName,
                        size = 16.dp,
                        color = if (selected) c else TeddTokens.InkMuted,
                        strokeWidth = 1.8.dp,
                    )
                }
            }
        }
    }
}

internal fun parseHexCrud(hex: String): Long {
    val clean = hex.trimStart('#')
    val rgb = clean.toLong(16)
    return 0xFF000000L or rgb
}

/**
 * Rattachement d'un projet à une compétence : le temps passé sur ses tâches compte
 * dans les heures de la compétence, au même titre que le temps d'étude.
 */
@Composable
private fun ProjectSkillPicker(skills: List<Skill>, selectedId: String?, onSelect: (String) -> Unit) {
    Column {
        Text("COMPÉTENCE", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 2.dp))
        Text(
            text = "Le temps passé à construire comptera dans ses heures.",
            color = TeddTokens.InkMuted,
            fontSize = 11.sp,
            modifier = Modifier.padding(bottom = 6.dp),
        )
        FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            skills.forEach { sk ->
                val selected = sk.id == selectedId
                val c = runCatching { Color(parseHexCrud(sk.color)) }.getOrElse { TeddTokens.LimeStrong }
                Row(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RFull))
                        .background(if (selected) c.copy(alpha = 0.12f) else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onSelect(sk.id) },
                        )
                        .padding(horizontal = 12.dp, vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    TeddIcon(name = sk.icon, size = 12.dp, color = if (selected) c else TeddTokens.InkMuted, strokeWidth = 2.dp)
                    Text(
                        text = sk.name,
                        color = if (selected) c else TeddTokens.Ink,
                        fontSize = 12.5.sp,
                        fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
                    )
                }
            }
        }
    }
}
